/**
 * ShareApp — the `/share` LAPTOP web-remote (reached via the room's share code,
 * e.g. share.8x8.vc/<code>). This is the spaces port of spot-client's
 * `spot-remote/ui/views/share/`: a laptop user opens the share code and screen-
 * shares INTO the room's meeting. Unlike the in-room control iPad (which only
 * drives the TV over the DO), this surface loads the full lib-jitsi-meet and runs
 * a `ProxyConnectionService` P2P link to the Spot-TV — so the laptop's screen
 * appears in the meeting as the room's share, exactly as legacy Spot did it.
 *
 * Flow: enter/auto the share code -> POST /pair -> connect the room DO as a remote
 * -> learn the Spot-TV's endpoint id from the roster -> capture the screen and pipe
 * it to the TV over the proxy connection, with Jingle signalling relayed as
 * `MESSAGES.CLIENT_PROXY_MESSAGE` (out) / `MESSAGES.JITSI_MEET_UPDATE` (in).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MessageEnvelope, RoomOccupant, SpotTvPresence } from '@spaces/contracts';
import { COMMANDS } from '@spaces/contracts';
import { getConfig } from '../config.js';
import { getDeviceId } from '../device-id.js';
import { log } from '../log.js';
import { meetingNameForRoom } from '../meeting-name.js';
import { PairingClient, PairingError } from '../transport/pairing-client.js';
import { RemoteControlService } from '../transport/RemoteControlService.js';
import { ScreenShare, Monitor, X } from '../ui/icons.js';
import { Button, Card, CodeInput, Dot, Logo, Screen, Shell, StatusLine } from '../ui/components.js';
import { ScreenshareSession, type ShareState } from './screenshare-session.js';

type Phase = 'entry' | 'pairing' | 'ready' | 'error';

function spotIdOf(occupants: RoomOccupant[]): string {
    return occupants.find(o => o.clientType === 'spot-tv')?.endpointId ?? '';
}

export function ShareApp(): JSX.Element {
    const config = useMemo(() => getConfig(), []);
    const deviceId = useMemo(() => getDeviceId('remote'), []);
    const pairing = useMemo(() => new PairingClient('/api'), []);

    const [phase, setPhase] = useState<Phase>('entry');
    const [status, setStatus] = useState<string>('Enter the share code from the room screen');
    const [keyInput, setKeyInput] = useState<string>('');
    const [roomName, setRoomName] = useState<string>('');
    const [presence, setPresence] = useState<SpotTvPresence | null>(null);
    const [shareState, setShareState] = useState<ShareState>('idle');

    const rcsRef = useRef<RemoteControlService | null>(null);
    const sessionRef = useRef<ScreenshareSession | null>(null);
    const spotIdRef = useRef<string>('');
    const roomIdRef = useRef<string>('');

    const inMeeting = Boolean(presence && presence.view === 'meeting' && presence.inMeeting);

    /** Tear down any live screenshare connection (the session resets shareState). */
    const teardownShare = useCallback((reason: string) => {
        sessionRef.current?.stop(reason);
    }, []);

    /** Begin capturing the laptop screen and proxying it to the room's meeting. */
    const startShare = useCallback(async () => {
        const rcs = rcsRef.current;
        const session = sessionRef.current;
        const spotId = spotIdRef.current;

        if (!rcs || !session) {
            return;
        }
        if (!spotId) {
            setStatus('The room TV is not connected yet — try again in a moment.');

            return;
        }

        // The proxy feeds the screen into the TV's MEETING, so the room must be in one.
        if (!inMeeting) {
            setStatus('Starting the room meeting…');
            rcs.sendCommand(COMMANDS.GO_TO_MEETING, { meetingName: meetingNameForRoom(roomIdRef.current) })
                .catch(err => log.warn('share goToMeeting failed', { error: String(err) }));
            // The user taps Share again once the room shows "in a meeting".
            return;
        }

        await session.start(spotId);
    }, [inMeeting]);

    const stopShare = useCallback(() => {
        teardownShare('user-stopped');
        setStatus('Screen sharing stopped.');
    }, [teardownShare]);

    const doPair = useCallback(async (key: string) => {
        const trimmed = key.trim();

        if (!trimmed) {
            return;
        }

        setPhase('pairing');
        setStatus('Connecting to the room…');

        try {
            const pair = await pairing.pair(trimmed, deviceId);

            roomIdRef.current = pair.room.id;

            const rcs = new RemoteControlService({
                roomDoUrl: config.roomDoUrl,
                roomId: pair.room.id,
                endpointId: deviceId,
                accessToken: pair.accessToken,
                clientType: 'remote-temp',
                joinCode: trimmed
            });

            rcs.on('presence', s => setPresence(s));
            rcs.on('roster', occupants => { spotIdRef.current = spotIdOf(occupants); });
            rcs.on('message', (frame: MessageEnvelope) => sessionRef.current?.handleMessage(frame));
            rcs.on('error', frame => setStatus(`Room error: ${frame.message}`));
            rcs.on('disconnect', () => {
                teardownShare('disconnect');
                setStatus('Disconnected from the room.');
                setPhase('error');
            });

            await rcs.connect();
            rcsRef.current = rcs;
            sessionRef.current = new ScreenshareSession({
                externalApiSrc: config.externalApiSrc,
                deviceId,
                rcs,
                accessToken: pair.accessToken,
                onState: setShareState,
                onStatus: setStatus
            });

            setRoomName(pair.room.name);
            setPhase('ready');
            setStatus(`Connected to ${pair.room.name}`);
        } catch (err) {
            const message = err instanceof PairingError ? `${err.message} (${err.code})` : (err as Error).message;

            log.error('share pairing failed', { error: message });
            setStatus(`Could not connect: ${message}`);
            setPhase('entry');
        }
    }, [config.externalApiSrc, config.roomDoUrl, deviceId, pairing, teardownShare]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlKey = params.get('key') ?? params.get('share');

        if (urlKey) {
            setKeyInput(urlKey);
            void doPair(urlKey);
        }
    }, []);

    useEffect(() => () => {
        teardownShare('unmount');
        rcsRef.current?.disconnect();
    }, [teardownShare]);

    if (phase === 'ready') {
        const sharing = shareState === 'sharing';

        return (
            <Screen role = "remote">
                <Shell>
                    <Card>
                        <div className = "stack">
                            <div className = "row">
                                <Logo />
                                <span className = "eyebrow">Share to room</span>
                                <span className = "spacer" />
                                <Dot kind = { spotIdRef.current ? 'live' : 'idle' } />
                            </div>

                            <div className = "share-room">
                                <Monitor />
                                <div>
                                    <div className = "share-room__name">{ roomName }</div>
                                    <div className = "share-room__sub">
                                        { inMeeting ? 'In a meeting' : 'Idle — sharing will start the meeting' }
                                    </div>
                                </div>
                            </div>

                            { sharing ? (
                                <Button
                                    label = "Stop sharing"
                                    variant = "danger"
                                    icon = { <X /> }
                                    block = { true }
                                    large = { true }
                                    onClick = { stopShare }
                                    testId = "share-stop" />
                            ) : (
                                <Button
                                    label = { shareState === 'starting' ? 'Starting…' : 'Share your screen' }
                                    icon = { <ScreenShare /> }
                                    block = { true }
                                    large = { true }
                                    disabled = { shareState === 'starting' }
                                    onClick = { () => void startShare() }
                                    testId = "share-start" />
                            ) }

                            <StatusLine tone = { sharing ? 'ok' : 'info' } text = { status } />
                        </div>
                    </Card>
                </Shell>
            </Screen>
        );
    }

    return (
        <Screen role = "remote">
            <Shell>
                <Card>
                    <div className = "stack">
                        <div className = "row">
                            <Logo />
                            <span className = "eyebrow">Share to room</span>
                        </div>
                        <StatusLine tone = { phase === 'error' ? 'err' : 'info' } text = { status } />
                        <CodeInput
                            value = { keyInput }
                            length = { 6 }
                            onChange = { setKeyInput }
                            onEnter = { () => void doPair(keyInput) }
                            autoFocus = { true }
                            testId = "share-code-input" />
                        <Button
                            label = "Connect"
                            block = { true }
                            disabled = { phase === 'pairing' }
                            onClick = { () => void doPair(keyInput) }
                            testId = "share-connect" />
                    </div>
                </Card>
            </Shell>
        </Screen>
    );
}
